import {Link} from 'react-router-dom';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {useAdminStats} from '@/hooks/useAdminStats';

const STATUS_CONFIG = [
    {key: 'active', label: 'Active', tone: 'active'},
    {key: 'provisioning', label: 'Provisioning', tone: 'provisioning'},
    {key: 'suspended', label: 'Suspended', tone: 'suspended'},
    {key: 'failed', label: 'Failed', tone: 'failed'}
] as const;

function LoadingState() {
    return (
        <div aria-label="Loading platform overview" className="studio-admin-loading" role="status">
            <div className="studio-admin-overview-grid">{['users', 'sites', 'health'].map(item => <span key={item} />)}</div>
            <div className="studio-admin-loading-panel" />
        </div>
    );
}

export function AdminDashboardPage() {
    const {data: stats, isLoading, isError, error, refetch} = useAdminStats();
    const attentionCount = stats ? stats.sites.provisioning + stats.sites.suspended + stats.sites.failed : 0;

    return (
        <div className="studio-content-page studio-admin-page">
            <header className="studio-page-heading studio-admin-page-heading">
                <div>
                    <p className="studio-eyebrow">Platform control</p>
                    <h1>Admin overview</h1>
                    <p className="studio-admin-intro">Understand platform growth, publication health, and the work that needs attention.</p>
                </div>
            </header>

            {isError && <div className="studio-admin-error" role="alert"><div><LucideIcon.CircleAlert size={19} /><span>{error instanceof Error ? error.message : 'Failed to load platform statistics'}</span></div><button className="studio-control studio-button-secondary" type="button" onClick={() => refetch()}>Try again</button></div>}
            {isLoading && <LoadingState />}

            {stats && <>
                <section aria-label="Platform overview" className="studio-admin-overview-grid">
                    <Link to="/superadmin/users">
                        <div><span>Registered accounts</span><strong>{formatNumber(stats.users.total)}</strong><p>People with platform access</p></div>
                        <i><LucideIcon.UsersRound size={21} /></i>
                        <LucideIcon.ArrowRight className="studio-admin-stat-arrow" size={16} />
                    </Link>
                    <Link to="/superadmin/sites">
                        <div><span>Active publications</span><strong>{formatNumber(stats.sites.active)}</strong><p>{formatNumber(stats.sites.total)} publications in total</p></div>
                        <i><LucideIcon.Newspaper size={21} /></i>
                        <LucideIcon.ArrowRight className="studio-admin-stat-arrow" size={16} />
                    </Link>
                    <article className={attentionCount ? 'is-attention' : ''}>
                        <div><span>Needs attention</span><strong>{formatNumber(attentionCount)}</strong><p>{attentionCount ? 'Review publication health below' : 'Everything is operating normally'}</p></div>
                        <i><LucideIcon.Activity size={21} /></i>
                    </article>
                </section>

                <section className="studio-admin-overview-layout">
                    <div className="studio-admin-health-panel">
                        <div className="studio-admin-section-heading"><div><p className="studio-eyebrow">Publication health</p><h2>Publication status</h2></div><span>{formatNumber(stats.sites.total)} total</span></div>
                        <div className="studio-admin-health-list">
                            {STATUS_CONFIG.map(({key, label, tone}) => {
                                const value = stats.sites[key];
                                const percentage = stats.sites.total ? Math.round((value / stats.sites.total) * 100) : 0;
                                return (
                                    <div key={key} className={`studio-admin-health-row is-${tone}`}>
                                        <div><i /><span>{label}</span></div>
                                        <div className="studio-admin-health-progress"><i style={{width: `${percentage}%`}} /></div>
                                        <strong>{formatNumber(value)}</strong>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <aside className="studio-admin-quick-panel">
                        <p className="studio-eyebrow">Quick management</p>
                        <h2>Platform operations</h2>
                        <p>Move directly into the area you need to review.</p>
                        <nav aria-label="Admin quick actions">
                            <Link to="/superadmin/users"><span><LucideIcon.UsersRound size={17} />Manage users</span><LucideIcon.ArrowRight size={15} /></Link>
                            <Link to="/superadmin/sites"><span><LucideIcon.Newspaper size={17} />Review publications</span><LucideIcon.ArrowRight size={15} /></Link>
                            <Link to="/superadmin/plans"><span><LucideIcon.Crown size={17} />Configure plans</span><LucideIcon.ArrowRight size={15} /></Link>
                            <Link to="/superadmin/marketplace"><span><LucideIcon.LayoutTemplate size={17} />Curate marketplace</span><LucideIcon.ArrowRight size={15} /></Link>
                        </nav>
                    </aside>
                </section>
            </>}
        </div>
    );
}
